import type { ProfileSectionKind } from './profile-sections';

/** Canonical v1 display order for public resume profiles (tie-breaker after position). */
export const CANONICAL_PROFILE_SECTION_ORDER: readonly ProfileSectionKind[] = [
  'gallery_representation',
  'education',
  'exhibition',
  'press',
  'career',
  'studio',
  'tools',
  'other_media',
] as const;

export type ProfileSectionItemLike = {
  id: string;
  position: number;
};

export type ProfileSectionLike = {
  id: string;
  kind: string;
  position: number;
  isPublic: boolean;
  /** Marked "doesn't apply to me" (#4153). Still never rendered publicly — it has no items. */
  isNotApplicable?: boolean;
  items: ProfileSectionItemLike[];
};

const canonicalIndex = new Map<string, number>(CANONICAL_PROFILE_SECTION_ORDER.map((kind, index) => [kind, index]));

function canonicalRank(kind: string): number {
  return canonicalIndex.get(kind) ?? CANONICAL_PROFILE_SECTION_ORDER.length;
}

/**
 * Sort sections by stored position, then canonical kind order for ties.
 */
export function sortProfileSections<T extends ProfileSectionLike>(sections: T[]): T[] {
  return [...sections].sort((a, b) => {
    if (a.position !== b.position) {
      return a.position - b.position;
    }
    return canonicalRank(a.kind) - canonicalRank(b.kind);
  });
}

/**
 * Public visitors only see public sections with at least one item.
 * Owners receive all sections from the API (including empty); empty sections still
 * render nothing in the read-only surface until edit UI adds empty states.
 */
export function filterSectionsForPublicViewer<T extends ProfileSectionLike>(sections: T[]): T[] {
  return sections.filter((section) => section.isPublic && section.items.length > 0);
}

/** Sections with no items never render in the read-only resume stack. */
export function filterSectionsWithItems<T extends ProfileSectionLike>(sections: T[]): T[] {
  return sections.filter((section) => section.items.length > 0);
}

export function orderResumeSectionsForDisplay<T extends ProfileSectionLike>(
  sections: T[],
  options: { isOwnerView: boolean }
): T[] {
  const visible = options.isOwnerView ? filterSectionsWithItems(sections) : filterSectionsForPublicViewer(sections);
  return sortProfileSections(visible);
}

export type ResumeSectionPlaceholder = ProfileSectionLike & {
  isPlaceholder: true;
};

export type ResumeSectionSlot<T extends ProfileSectionLike = ProfileSectionLike> = T | ResumeSectionPlaceholder;

export function isResumeSectionPlaceholder(section: ResumeSectionSlot): section is ResumeSectionPlaceholder {
  return 'isPlaceholder' in section && section.isPlaceholder === true;
}

/** Owner edit surface: one slot per v1 kind, with placeholders for kinds not yet provisioned. */
export function buildOwnerResumeSectionSlots<T extends ProfileSectionLike>(sections: T[]): ResumeSectionSlot<T>[] {
  const byKind = new Map(sections.map((section) => [section.kind, section]));

  return CANONICAL_PROFILE_SECTION_ORDER.map((kind, index) => {
    const existing = byKind.get(kind);
    if (existing) {
      return existing;
    }

    return {
      id: `placeholder-${kind}`,
      kind,
      position: index,
      isPublic: true,
      items: [],
      isPlaceholder: true as const,
    };
  });
}

/** Sort owner slots by stored position (placeholders keep canonical index as position). */
export function orderResumeSectionsForOwnerEdit<T extends ProfileSectionLike>(sections: T[]): ResumeSectionSlot<T>[] {
  return sortProfileSections(buildOwnerResumeSectionSlots(sections));
}

/** Owner drag-reorder: arrange slots in an explicit kind order (includes placeholders). */
export function orderOwnerSectionSlotsByKind<T extends ProfileSectionLike>(
  sections: T[],
  kindOrder: readonly ProfileSectionKind[]
): ResumeSectionSlot<T>[] {
  const slots = buildOwnerResumeSectionSlots(sections);
  const byKind = new Map(slots.map((slot) => [slot.kind, slot]));
  return kindOrder.map((kind) => byKind.get(kind)).filter((slot): slot is ResumeSectionSlot<T> => slot != null);
}

/**
 * Rewrite persisted section positions to match a drag reorder.
 *
 * `kindOrder` is the full owner ordering (all eight kinds, placeholders
 * included), but `reorderSections` on the server stores each section's position
 * as its index in the submitted id list — dense `0..n-1` over persisted sections
 * only. Mirror that here: rank the persisted sections by their slot in
 * `kindOrder`, then assign the same dense positions. Using the raw kind-slot
 * index instead would leave optimistic state sparse (e.g. press → 3) while the
 * DB holds dense values, so the two disagree after the next refetch.
 */
export function applySectionPositionsFromKindOrder<T extends ProfileSectionLike>(
  sections: T[],
  kindOrder: readonly ProfileSectionKind[]
): T[] {
  const rankByKind = new Map(kindOrder.map((kind, index) => [kind, index]));
  const densePositionByKind = new Map<string, number>();
  sections
    .filter((section) => rankByKind.has(section.kind as ProfileSectionKind))
    .sort((a, b) => rankByKind.get(a.kind as ProfileSectionKind)! - rankByKind.get(b.kind as ProfileSectionKind)!)
    .forEach((section, index) => densePositionByKind.set(section.kind, index));

  return sections.map((section) => ({
    ...section,
    position: densePositionByKind.get(section.kind) ?? section.position,
  }));
}

/**
 * Persisted section ids in display order.
 *
 * Skips kinds with no ProfileSection row, including a provisional placeholder
 * inserted while "doesn't apply to me" is still in flight — that id was never
 * stored, and `reorderSections` would reject it.
 */
export function persistedSectionIdsFromKindOrder(
  kindOrder: readonly ProfileSectionKind[],
  sections: ProfileSectionLike[]
): string[] {
  const byKind = new Map(sections.map((section) => [section.kind, section]));
  return kindOrder
    .map((kind) => byKind.get(kind))
    .filter((section): section is ProfileSectionLike => section != null && !section.id.startsWith('placeholder-'))
    .map((section) => section.id);
}
